import vendorService from "../../../../services/procurementService/vendorService";
import { CACHE_KEY_VENDORS } from "../../../../utils/constants";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { VendorInterface } from "../../../../models/ProcurementModule/Vendor";

interface UpdateVendorContext {
  previousVendors: VendorInterface[];
}

const useUpdateVendors = (page: number, pageSize: number) => {
  const queryClient = useQueryClient();

  return useMutation<
    VendorInterface,
    Error,
    VendorInterface,
    UpdateVendorContext
  >({
    mutationFn: async (vendor: VendorInterface) => {
      // Assuming the ID is stored in the vendor object or can be derived
      const { id, ...vendorData } = vendor;
      return vendorService.Update(id || "", vendorData);
    },

    onMutate: async (updatedVendor: VendorInterface) => {
      await queryClient.cancelQueries({
        queryKey: [CACHE_KEY_VENDORS, page, pageSize],
      });

      const previousVendors =
        queryClient.getQueryData<VendorInterface[]>([
          CACHE_KEY_VENDORS,
          page,
          pageSize,
        ]) || [];

      queryClient.setQueryData<VendorInterface[]>(
        [CACHE_KEY_VENDORS, page, pageSize],
        (vendors = []) =>
          vendors.map((vendor) =>
            vendor.id === updatedVendor.id ? updatedVendor : vendor
          )
      );

      return { previousVendors };
    },

    onSuccess: (savedVendor) => {
      queryClient.setQueryData<VendorInterface[]>(
        [CACHE_KEY_VENDORS, page, pageSize],
        (vendors) =>
          vendors?.map((vendor) =>
            vendor.id === savedVendor.id ? savedVendor : vendor
          )
      );
    },

    onError: (error, updatedVendor, context) => {
      if (!context) return;

      queryClient.setQueryData<VendorInterface[]>(
        [CACHE_KEY_VENDORS, page, pageSize],
        context.previousVendors
      );
    },
  });
};

export default useUpdateVendors;
